const ID3_HEADER_SIZE = 10;
const ID3_FRAME_HEADER_SIZE = 10;
const ID3_IDENTIFIER = 'ID3';
const PRIV_FRAME_ID = 'PRIV';

export const AI_VOICE_CANONICAL_GZIP_OWNER = 'creator-web/google-tts-canonical-gzip-v1';
export const AI_VOICE_ORIGIN_OWNER = 'creator-web/google-tts-origin-v1';
export const MAX_CANONICAL_GZIP_PAYLOAD_BYTES = 256 * 1024;

export interface TtsOriginMetadata {
  courseID: string;
  pageID: string;
}

export interface TtsMp3MetadataPayload {
  canonicalGzipPayload: Uint8Array | null;
  origin: TtsOriginMetadata | null;
}

type StreamCompressionConstructor = new (format: string) => unknown;

const getGlobalCompressionConstructors = () =>
  globalThis as typeof globalThis & {
    CompressionStream?: StreamCompressionConstructor;
    DecompressionStream?: StreamCompressionConstructor;
  };

const bytesToAscii = (bytes: Uint8Array): string => {
  let result = '';
  for (let i = 0; i < bytes.length; i++) {
    result += String.fromCharCode(bytes[i]);
  }
  return result;
};

const asciiToBytes = (value: string): Uint8Array => {
  const result = new Uint8Array(value.length);
  for (let i = 0; i < value.length; i++) {
    result[i] = value.charCodeAt(i);
  }
  return result;
};

const readSyncSafeInt = (bytes: Uint8Array, offset: number): number =>
  ((bytes[offset] & 0x7f) << 21) | ((bytes[offset + 1] & 0x7f) << 14) | ((bytes[offset + 2] & 0x7f) << 7) | (bytes[offset + 3] & 0x7f);

const writeSyncSafeInt = (value: number): Uint8Array =>
  new Uint8Array([(value >> 21) & 0x7f, (value >> 14) & 0x7f, (value >> 7) & 0x7f, value & 0x7f]);

const readUint32BE = (bytes: Uint8Array, offset: number): number =>
  (((bytes[offset] << 24) >>> 0) | (bytes[offset + 1] << 16) | (bytes[offset + 2] << 8) | bytes[offset + 3]) >>> 0;

const writeUint32BE = (value: number): Uint8Array =>
  new Uint8Array([(value >>> 24) & 0xff, (value >>> 16) & 0xff, (value >>> 8) & 0xff, value & 0xff]);

const concatBytes = (chunks: Uint8Array[]): Uint8Array => {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
};

const bytesToArrayBuffer = (bytes: Uint8Array): ArrayBuffer => {
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return buffer;
};

const splitId3AndAudio = (bytes: Uint8Array): { id3Bytes: Uint8Array | null; audioBytes: Uint8Array } => {
  if (bytes.length < ID3_HEADER_SIZE || bytesToAscii(bytes.subarray(0, 3)) !== ID3_IDENTIFIER) {
    return { id3Bytes: null, audioBytes: bytes };
  }

  const flags = bytes[5];
  const tagSize = readSyncSafeInt(bytes, 6);
  const footerSize = (flags & 0x10) !== 0 ? ID3_HEADER_SIZE : 0;
  const tagEnd = Math.min(bytes.length, ID3_HEADER_SIZE + tagSize + footerSize);
  return {
    id3Bytes: bytes.subarray(0, tagEnd),
    audioBytes: bytes.subarray(tagEnd),
  };
};

type ParsedFrame = {
  id: string;
  data: Uint8Array;
};

const parseId3Frames = (id3Bytes: Uint8Array): ParsedFrame[] => {
  const frames: ParsedFrame[] = [];
  if (id3Bytes.length < ID3_HEADER_SIZE || bytesToAscii(id3Bytes.subarray(0, 3)) !== ID3_IDENTIFIER) {
    return frames;
  }

  const tagSize = readSyncSafeInt(id3Bytes, 6);
  const tagEnd = Math.min(id3Bytes.length, ID3_HEADER_SIZE + tagSize);
  let offset = ID3_HEADER_SIZE;

  while (offset + ID3_FRAME_HEADER_SIZE <= tagEnd) {
    const frameId = bytesToAscii(id3Bytes.subarray(offset, offset + 4));
    if (!/^[A-Z0-9]{4}$/.test(frameId)) {
      break;
    }

    const frameSize = readUint32BE(id3Bytes, offset + 4);
    if (frameSize <= 0) {
      break;
    }

    const frameDataStart = offset + ID3_FRAME_HEADER_SIZE;
    const frameDataEnd = frameDataStart + frameSize;
    if (frameDataEnd > tagEnd) {
      break;
    }

    frames.push({
      id: frameId,
      data: id3Bytes.subarray(frameDataStart, frameDataEnd),
    });

    offset = frameDataEnd;
  }

  return frames;
};

const buildFrame = (id: string, payload: Uint8Array): Uint8Array => {
  const header = concatBytes([asciiToBytes(id), writeUint32BE(payload.length), new Uint8Array([0x00, 0x00])]);
  return concatBytes([header, payload]);
};

const buildId3Tag = (frames: Uint8Array[]): Uint8Array => {
  const frameBlob = concatBytes(frames);
  const header = concatBytes([asciiToBytes(ID3_IDENTIFIER), new Uint8Array([0x03, 0x00, 0x00]), writeSyncSafeInt(frameBlob.length)]);
  return concatBytes([header, frameBlob]);
};

const buildPrivFramePayload = (owner: string, payload: Uint8Array): Uint8Array => {
  const ownerBytes = asciiToBytes(owner);
  return concatBytes([ownerBytes, new Uint8Array([0x00]), payload]);
};

const tryReadPrivPayload = (frameData: Uint8Array): { owner: string; payload: Uint8Array } | null => {
  const nullIndex = frameData.indexOf(0x00);
  if (nullIndex < 0) return null;

  const owner = bytesToAscii(frameData.subarray(0, nullIndex));
  const payload = frameData.subarray(nullIndex + 1);
  return { owner, payload };
};

const toOriginJsonBytes = (origin: { courseID: string | number; pageID: string | number }): Uint8Array => {
  const raw = JSON.stringify({
    courseID: String(origin.courseID),
    pageID: String(origin.pageID),
  });
  return new TextEncoder().encode(raw);
};

const tryReadOriginJson = (payload: Uint8Array): TtsOriginMetadata | null => {
  try {
    const decoded = new TextDecoder().decode(payload);
    const parsed = JSON.parse(decoded) as Partial<TtsOriginMetadata>;
    if (typeof parsed.courseID !== 'string' || typeof parsed.pageID !== 'string') return null;
    if (!parsed.courseID || !parsed.pageID) return null;
    return {
      courseID: parsed.courseID,
      pageID: parsed.pageID,
    };
  } catch {
    return null;
  }
};

export const isGzipMetadataSupported = (): boolean => {
  const globalWithCompression = getGlobalCompressionConstructors();

  return typeof globalWithCompression.CompressionStream !== 'undefined' && typeof globalWithCompression.DecompressionStream !== 'undefined';
};

export const gzipUtf8 = async (value: string): Promise<Uint8Array> => {
  const globalWithCompression = getGlobalCompressionConstructors();

  if (typeof globalWithCompression.CompressionStream === 'undefined') {
    throw new Error('CompressionStream is not supported in this browser.');
  }

  const encoder = new TextEncoder();
  const inputBytes = encoder.encode(value);
  const inputBlob = new Blob([bytesToArrayBuffer(inputBytes)], { type: 'application/octet-stream' });
  const compressedStream = inputBlob.stream().pipeThrough(new globalWithCompression.CompressionStream('gzip') as any);
  const compressedBuffer = await new Response(compressedStream).arrayBuffer();
  return new Uint8Array(compressedBuffer);
};

export const gunzipToUtf8 = async (value: Uint8Array): Promise<string> => {
  const globalWithCompression = getGlobalCompressionConstructors();

  if (typeof globalWithCompression.DecompressionStream === 'undefined') {
    throw new Error('DecompressionStream is not supported in this browser.');
  }

  const inputBlob = new Blob([bytesToArrayBuffer(value)], { type: 'application/octet-stream' });
  const decompressedStream = inputBlob.stream().pipeThrough(new globalWithCompression.DecompressionStream('gzip') as any);
  const decompressedBuffer = await new Response(decompressedStream).arrayBuffer();
  return new TextDecoder().decode(decompressedBuffer);
};

export const addCanonicalGzipMetadataToMp3Blob = async (
  blob: Blob,
  canonicalTtsText: string,
  origin?: { courseID: string | number; pageID: string | number },
): Promise<Blob> => {
  if (!isGzipMetadataSupported()) {
    throw new Error('Gzip metadata is not supported in this browser.');
  }

  const payloadBytes = await gzipUtf8(canonicalTtsText);
  if (payloadBytes.length > MAX_CANONICAL_GZIP_PAYLOAD_BYTES) {
    throw new Error('Gzip payload exceeds metadata size limit.');
  }

  const fileBytes = new Uint8Array(await blob.arrayBuffer());
  const { id3Bytes, audioBytes } = splitId3AndAudio(fileBytes);
  const existingFrames = id3Bytes ? parseId3Frames(id3Bytes) : [];

  const retainedFrames = existingFrames.filter((frame) => {
    if (frame.id !== PRIV_FRAME_ID) return true;
    const priv = tryReadPrivPayload(frame.data);
    return priv?.owner !== AI_VOICE_CANONICAL_GZIP_OWNER && priv?.owner !== AI_VOICE_ORIGIN_OWNER;
  });

  const nextFrames = [
    ...retainedFrames.map((frame) => buildFrame(frame.id, frame.data)),
    buildFrame(PRIV_FRAME_ID, buildPrivFramePayload(AI_VOICE_CANONICAL_GZIP_OWNER, payloadBytes)),
  ];
  if (origin) {
    nextFrames.push(buildFrame(PRIV_FRAME_ID, buildPrivFramePayload(AI_VOICE_ORIGIN_OWNER, toOriginJsonBytes(origin))));
  }

  const id3Tag = buildId3Tag(nextFrames);
  return new Blob([bytesToArrayBuffer(id3Tag), bytesToArrayBuffer(audioBytes)], { type: blob.type || 'audio/mpeg' });
};

export const readTtsMetadataFromMp3Buffer = (buffer: ArrayBuffer): TtsMp3MetadataPayload => {
  const bytes = new Uint8Array(buffer);
  const { id3Bytes } = splitId3AndAudio(bytes);
  if (!id3Bytes) {
    return { canonicalGzipPayload: null, origin: null };
  }

  let canonicalGzipPayload: Uint8Array | null = null;
  let origin: TtsOriginMetadata | null = null;

  const frames = parseId3Frames(id3Bytes);
  for (const frame of frames) {
    if (frame.id !== PRIV_FRAME_ID) continue;
    const priv = tryReadPrivPayload(frame.data);
    if (!priv) continue;

    if (priv.owner === AI_VOICE_CANONICAL_GZIP_OWNER) {
      canonicalGzipPayload = priv.payload;
      continue;
    }

    if (priv.owner === AI_VOICE_ORIGIN_OWNER) {
      origin = tryReadOriginJson(priv.payload);
    }
  }

  return { canonicalGzipPayload, origin };
};

export const readTtsMetadataFromMp3Url = async (url: string, signal?: AbortSignal): Promise<TtsMp3MetadataPayload> => {
  const response = await fetch(url, {
    signal,
    cache: 'no-store',
    credentials: 'same-origin',
  });

  if (!response.ok) {
    throw new Error(`Unable to fetch audio metadata. Status: ${response.status}`);
  }

  const buffer = await response.arrayBuffer();
  return readTtsMetadataFromMp3Buffer(buffer);
};

